'use client';

import { useState } from 'react';
import { PASSWORD_MIN_LENGTH, readableQuery } from '@content-removal/shared';
import { ApiError, api } from './api';
import { API_URL } from './env';
import { Notice, Wordmark } from './components';
import { Input, PasswordInput } from './form';
import { ArrowRightIcon, GoogleIcon, LockIcon } from './icons';

/**
 * Sign-in, shared by the client app and the support app.
 *
 * The two differ only in which door they knock on, whether visitors may create
 * an account and where they land afterwards, so those are props rather than a
 * second copy of the form.
 *
 * Creating an account asks for one thing here — the address — then leaves:
 * the code is `/verify-email`'s own page, and platform-and-handle is
 * `/profiles/start`'s. That used to be three fields on this one component
 * swapped in and out of the same card, and it worked until somebody hit
 * refresh mid-code: the whole state lived in this component's memory, so the
 * reload put them back on the email field with a code already sitting in
 * their inbox and no way to say so. A real URL per step means the browser's
 * own reload button, back button and address bar all keep working — `/verify-
 * email?email=…` still knows whose code it is asking for, and `/profiles/
 * start` checks the session rather than trusting anything this card decided a
 * moment ago. The panel's login (`allowSignUp={false}`) never sees any of
 * this: it is one field, one password, unchanged.
 *
 * What this no longer does is check the role it got back and sign itself out
 * again when the answer was the wrong kind of account. That check ran after the
 * server had already handed over a session, so a browser holding an older
 * bundle skipped it and landed inside; and it could only exist because both
 * products shared one door. Each has its own now, and each refuses the other's
 * accounts before any session exists.
 */
function googleErrorMessage(code: string): string | null {
  if (code === 'google_unavailable') return 'Google sign-in is not turned on here yet.';
  if (code === 'google_failed') return "Couldn't sign in with Google. Try again.";
  return null;
}

const DEFAULT_WORDING: Wording = {
  user: 'Email',
  secret: 'Password',
  submit: 'Sign in',
  working: 'Working…',
};

type Wording = {
  user: string;
  secret: string;
  submit: string;
  working: string;
  /**
   * Shown instead of whatever the server said, when every way of failing should
   * look the same from outside. Without it the reason comes through, and the
   * reasons differ, which is how someone learns an address is a real account.
   */
  failure?: string;
};

export function SignInCard({
  context,
  brand,
  redirectTo,
  createRedirectTo,
  allowSignUp,
  switchHref,
  loginPath = '/auth/login',
  intro,
  wording = DEFAULT_WORDING,
  forgotPasswordHref,
  onSignedIn,
  belowSubmit,
  startInCreateMode,
  prefillHandle,
  prefillPlatform,
  prefillEmail,
  allowGoogle,
  initialError,
}: {
  /**
   * The product name over the form. Omit it and the card says nothing about
   * what it opens, which is what the support app wants: a page that only the
   * people who were sent to it can tell apart from any other login form.
   */
  context?: string;
  /** The product name over the context word. Left out, nothing is named. */
  brand?: string | null;
  redirectTo: string;
  /**
   * Where `/verify-email` sends a brand-new account once its code is
   * confirmed — `/profiles/start` by default in practice, decided there by
   * `AuthService.landingFor`, not by this card. Kept as a prop only because
   * `/verify-email` needs somewhere to fall back to if this were ever used
   * without it.
   */
  createRedirectTo?: string;
  allowSignUp: boolean;
  /**
   * Where the "Create an account" / "Sign in" link at the bottom goes —
   * `/sign-up` from `/sign-in` and back again. A real link rather than a
   * button that flips this card's own state, because the mode used to live
   * only in memory: a refresh right after pressing "Create an account" put
   * somebody back on the sign-in form with no sign anything had happened.
   * Required whenever `allowSignUp` is true; unused otherwise.
   */
  switchHref?: string;
  /**
   * This app's own sign-in endpoint. The customer app knocks on `/auth/login`,
   * the panel on `/admin/auth/login`, and each refuses the other's accounts
   * with the same words a wrong password gets. Which door was used is the only
   * thing that still distinguishes the two products to the API.
   */
  loginPath?: string;
  intro?: { signIn: string; create: string };
  /** What the two fields and the button are called. See {@link Wording}. */
  wording?: Wording;
  /**
   * Where "forgot your password" goes, or nothing and it is not offered.
   *
   * No default. The panel has no self-service reset -- a colleague's password
   * is reset by a colleague -- and a link that leads to a page which cannot
   * help is worse on that door than no link at all.
   */
  forgotPasswordHref?: string;
  onSignedIn: (path: string) => void;
  /** Rendered right under the submit button, inside the form. For the web
   *  app's demo-login links only — nothing else needs a fourth slot here. */
  belowSubmit?: React.ReactNode;
  /**
   * Opens on the create-account side rather than sign-in. For a door reached
   * only after somebody has already been shown their own results and pressed
   * "Unlock full report" — asking them to find the sign-up link first would
   * be one more click back through a decision they already made.
   */
  startInCreateMode?: boolean;
  /**
   * Where she posts and under what name, already typed somewhere before this
   * card — a handle box on the homepage, most likely. Carried through as query
   * params on the way to `/verify-email` and from there to `/profiles/start`,
   * which is what actually skips its own handle screen when these are set.
   * This card never reads them itself.
   */
  prefillHandle?: string;
  prefillPlatform?: string;
  /**
   * Starts the email field already filled — `/verify-email`'s "Use a
   * different email" link sends her back here with what she typed still in
   * it, since a typo is a letter to fix, not a field to retype from empty.
   */
  prefillEmail?: string;
  /**
   * Offers "Continue with Google" above the form. Only the customer doors do:
   * the panel's own login stays a plain password, one narrow door for a
   * small, known set of colleagues rather than one more identity provider to
   * administer. The button always renders when this is set — whether it
   * actually works depends on `GOOGLE_CLIENT_ID`/`SECRET` existing in this
   * environment, which the API alone knows, so a click here can still come
   * back to `?error=google_unavailable` rather than doing anything itself.
   */
  allowGoogle?: boolean;
  /** An error carried back on the URL, from Google's own round trip. */
  initialError?: string;
}) {
  // Which mode this render is: decided by which page rendered this card
  // (`startInCreateMode`, itself set from which of `/sign-in` / `/sign-up`
  // this is), never flipped in place any more.
  const creating = startInCreateMode ?? false;
  const [email, setEmail] = useState(prefillEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(
    initialError ? googleErrorMessage(initialError) : null,
  );
  const [busy, setBusy] = useState(false);

  const googleHref = (() => {
    const params = new URLSearchParams();
    if (prefillHandle) params.set('handle', prefillHandle);
    if (prefillPlatform) params.set('platform', prefillPlatform);
    const query = params.toString();
    return `${API_URL}/auth/google${query ? `?${query}` : ''}`;
  })();

  function goToVerify() {
    const params = new URLSearchParams({ email: email.trim() });
    if (prefillHandle) params.set('handle', prefillHandle);
    if (prefillPlatform) params.set('platform', prefillPlatform);
    onSignedIn(`/verify-email?${readableQuery(params)}`);
  }

  async function submitCreate(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (!email.trim()) {
      // Said the way every other blocked field in this product says it: what to
      // do, and what makes it valid — "Enter a profile name of at least 2
      // characters." rather than "Name too short". Two words are enough to
      // refuse; they are not enough to help.
      setError(wording.failure ?? 'Enter your email.');
      return;
    }

    setBusy(true);
    try {
      // No password: there is nothing yet to protect with one, and the account
      // this creates (or finds, if the address already has one) cannot do
      // anything until the code mailed to it comes back.
      await api('/auth/email-code', { method: 'POST', body: { email } });
      goToVerify();
    } catch (err) {
      // A 429 here means a code for this address is already on its way — the
      // ordinary shape of "Use a different email" retyping the one already
      // typed, or a double click. That is not a reason to trap her on this
      // field with nothing to do next: the code is real and `/verify-email`
      // is where it is typed, same as pressing "Resend" once already there.
      if (err instanceof ApiError && err.status === 429) {
        goToVerify();
        return;
      }
      setError(wording.failure ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submitLogin(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError(wording.failure ?? 'Fill in both fields.');
      return;
    }

    setBusy(true);
    try {
      // An account that belongs to the other product never gets this far: the
      // endpoint refuses it, in the same words a wrong password gets, before
      // any session exists.
      await api(loginPath, { method: 'POST', body: { email, password } });
      onSignedIn(redirectTo);
    } catch (err) {
      setError(wording.failure ?? (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const submit = creating ? submitCreate : submitLogin;
  /**
   * "Continue with email", not "Send code".
   *
   * It sits directly under "Continue with Google", and those two are the same
   * choice: which door you come in by. Written as one verb and one system word,
   * the pair read as different kinds of thing, and the button named what we were
   * about to do rather than what the person came for. What the code is, and that
   * it arrives by email, is said under the field instead — before the click,
   * which is where somebody hesitates, rather than on the next screen.
   */
  const submitLabel = creating ? 'Continue with email' : wording.submit;

  return (
    <main className="authmain">
      {/* Less room above the wordmark than beside it. Equal padding all round
          leaves the name sitting low in its own card: the eye reads the top
          edge and the first line as one block, and 32px of nothing between them
          is a gap, not a margin. What comes off the top is given to the space
          below, so the name stays a heading rather than the form's first row.
          With no wordmark there is nothing to make room for, so the padding is
          even again. */}
      <div className={`card authcard${context ? '' : ' authcard-plain'}${creating ? ' authcard-step' : ''}`}>
        {context && (
          <div className={intro ? 'auth-brand-tight' : 'auth-brand'}>
            <Wordmark context={context} name={brand ?? null} align="center" />
          </div>
        )}

        {intro && <p className="auth-intro">{creating ? intro.create : intro.signIn}</p>}

        {allowGoogle && (
          <>
            <a href={googleHref} className="btn btn-block auth-google-btn">
              <GoogleIcon />
              Continue with Google
            </a>
            <div className="auth-divider">
              <span>or</span>
            </div>
          </>
        )}

        {/* `noValidate` turns off the browser's own bubbles, not the rules: the
            fields keep `required` and `minLength` so assistive technology still
            announces them, and the checks that matter are the server's anyway. */}
        <form onSubmit={submit} noValidate>
          {/* `text`, not `email`. The value is still an address, but the type
              brings the browser's own "please include an @" with it, and that
              message names the field regardless of what the label calls it.
              `inputMode` keeps the right keyboard on a phone; `username` keeps
              password managers filling both fields as a pair. */}
          <Input
            id="account"
            label={wording.user}
            type="text"
            inputMode="email"
            autoComplete="username"
            required
            autoFocus={creating}
            value={email}
            /* Cleared as she types, not on the next submit. A refusal that
               stays under a field she has already filled in is the page arguing
               with what it can see, and it is the reason the button underneath
               looks disabled when it is not. */
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            /* On the create side the only failure there can be is about this
               field, and the field already keeps a line for it. Shown as a
               card-wide notice instead, it was a 44px block with its own padding
               between the field's own reserved line and the button — two gaps
               and a box for three words. */
            error={creating ? error : null}
          />

          {/* Only on the sign-in side: the create side asks for nothing but the
              address, so there is no password field to show, and nothing to
              have forgotten. */}
          {!creating && (
            <div className="auth-field">
              <PasswordInput
                id="secret"
                label={wording.secret}
                autoComplete="current-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                labelAction={
                  forgotPasswordHref ? (
                    <a href={forgotPasswordHref} className="auth-forgot">
                      Forgot?
                    </a>
                  ) : undefined
                }
              />
            </div>
          )}

          {/* Sign-in only. Its failures — a wrong password, an account that
              belongs to the other product — are about the pair of fields rather
              than either one, and the room is reserved even when empty so a
              failed submit does not drop the button a beat after the click. */}
          {!creating && (
            <div className="auth-error-slot">{error && <Notice tone="bad">{error}</Notice>}</div>
          )}

          <button
            className={`btn btn-primary btn-block${creating ? ' auth-submit-arrow' : ''}`}
            disabled={busy}
          >
            {busy ? wording.working : submitLabel}
            {creating && !busy && <ArrowRightIcon />}
          </button>

          {!creating && belowSubmit}
        </form>

        {/* One line, and about the fear rather than the plumbing.
            
            Somebody arriving here has had intimate material taken and is being
            asked for an address: what she needs at the button is that pressing
            it starts nothing anybody can see. That a six-digit code arrives is
            the next screen's business, which is where every email-first product
            says it, and the button above already says the address is how she is
            coming in. Carrying both in one line took an em dash to hold two
            unrelated clauses together, which is how a sentence admits it is two. */}
        {creating && (
          <p className="auth-trust">
            <LockIcon />
            Private. Nothing is filed without your say.
          </p>
        )}

        {/*
          The other door.

          These two used to sit on one row at equal weight, split left and
          right, and they answer different questions: one changes what this
          form is, the other rescues you from it. Side by side they read as a
          pair of equally likely next moves, which is why neither was easy to
          find when it was the one you wanted.

          So recovery went up beside the password, and the choice of door comes
          here, under a rule, as a sentence with one word to press. The
          question is asked first: a bare "Create an account instead" makes
          the reader work out what it is instead of.

          Nothing is rendered when an app does not offer sign-up.
        */}
        {allowSignUp && switchHref && (
          <p className="formswitch">
            {creating ? 'Already have an account? ' : 'New here? '}
            <a href={switchHref} className="linkbtn">
              {creating ? 'Sign in' : 'Create an account'}
            </a>
          </p>
        )}
      </div>
    </main>
  );
}
